<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import Button from 'dashboard/components-next/button/Button.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  selectedCount: { type: Number, default: 0 },
});

const emit = defineEmits(['close', 'confirm']);

const { t } = useI18n();
const store = useStore();

const selectedWebhookId = ref(null);
const selectedInboxId = ref(null);
const selectedTemplate = ref(null);
const templateSearch = ref('');
const customMessage = ref('');
const templateParserRef = ref(null);

const prospectWebhooks = useMapGetter('prospectWebhooks/getProspectWebhooks');
const allInboxes = useMapGetter('inboxes/getInboxes');
const getFilteredTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const selectedInbox = computed(() =>
  allInboxes.value.find(i => i.id === selectedInboxId.value)
);

const isWhatsAppInbox = computed(
  () => selectedInbox.value?.channel_type === INBOX_TYPES.WHATSAPP
);

const inboxTemplates = computed(() => {
  if (!selectedInboxId.value) return [];
  return getFilteredTemplates.value(selectedInboxId.value);
});

const filteredTemplates = computed(() => {
  if (!templateSearch.value) return inboxTemplates.value;
  return inboxTemplates.value.filter(tmpl =>
    tmpl.name.toLowerCase().includes(templateSearch.value.toLowerCase())
  );
});

const selectedWebhook = computed(() =>
  prospectWebhooks.value.find(w => w.id === selectedWebhookId.value)
);

const getTemplateBody = template => {
  const body = template.components.find(c => c.type === 'BODY');
  return body?.text || '';
};

const canConfirm = computed(() => {
  if (!selectedWebhookId.value) return false;
  if (!selectedInboxId.value) return true;
  if (isWhatsAppInbox.value) {
    if (selectedTemplate.value && templateParserRef.value?.v$?.$invalid)
      return false;
    return true;
  }
  return customMessage.value.trim().length > 0;
});

const handleTemplateSelect = template => {
  selectedTemplate.value = template;
};

const handleClearTemplate = () => {
  selectedTemplate.value = null;
  templateSearch.value = '';
};

const handleConfirm = () => {
  emit('confirm', {
    webhook: selectedWebhook.value,
    template: selectedTemplate.value || null,
    templateParams: templateParserRef.value?.processedParams || null,
    message: !isWhatsAppInbox.value ? customMessage.value.trim() : null,
    inboxId: selectedInboxId.value,
  });
};

const reset = () => {
  selectedWebhookId.value = null;
  selectedInboxId.value = null;
  selectedTemplate.value = null;
  templateSearch.value = '';
  customMessage.value = '';
};

watch(selectedInboxId, () => {
  selectedTemplate.value = null;
  templateSearch.value = '';
  customMessage.value = '';
});

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      store.dispatch('prospectWebhooks/get');
      reset();
    }
  }
);
</script>

<template>
  <woot-modal :show="show" :on-close="() => emit('close')">
    <div class="h-auto overflow-auto flex flex-col w-[34rem]">
      <woot-modal-header
        :header-title="t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.TITLE')"
        :header-content="
          t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.SUBTITLE', {
            count: selectedCount,
          })
        "
      />
      <div class="flex flex-col gap-4 px-8 pb-8 pt-4">
        <!-- Webhook selector -->
        <label>
          {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.WEBHOOK_LABEL') }}
          <span class="required-asterisk">*</span>
          <select v-model="selectedWebhookId" class="mt-1">
            <option :value="null" disabled>
              {{
                t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.WEBHOOK_PLACEHOLDER')
              }}
            </option>
            <option
              v-for="webhook in prospectWebhooks"
              :key="webhook.id"
              :value="webhook.id"
            >
              {{ webhook.name }}
            </option>
          </select>
          <span v-if="!prospectWebhooks.length" class="message">
            {{ t('CONTACTS_BULK_ACTIONS.NO_WEBHOOKS') }}
          </span>
        </label>

        <!-- Inbox selector -->
        <label>
          {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.INBOX_LABEL') }}
          <select v-model="selectedInboxId" class="mt-1">
            <option :value="null">
              {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.INBOX_PLACEHOLDER') }}
            </option>
            <option
              v-for="inbox in allInboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
        </label>

        <!-- When inbox selected -->
        <template v-if="selectedInboxId">
          <!-- WhatsApp official → template picker -->
          <div v-if="isWhatsAppInbox" class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.TEMPLATE_LABEL') }}
              </span>
              <button
                v-if="selectedTemplate"
                class="text-xs text-n-brand hover:underline"
                @click="handleClearTemplate"
              >
                {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.TEMPLATE_CHANGE') }}
              </button>
            </div>

            <!-- Selected template: parser with variable inputs -->
            <WhatsAppTemplateParser
              v-if="selectedTemplate"
              ref="templateParserRef"
              :template="selectedTemplate"
              @reset-template="handleClearTemplate"
            />

            <!-- Template list -->
            <div v-else class="flex flex-col gap-2">
              <div class="relative">
                <span
                  class="i-lucide-search absolute size-3.5 top-2.5 left-3 text-n-slate-10 pointer-events-none"
                />
                <input
                  v-model="templateSearch"
                  type="search"
                  class="placeholder:text-n-slate-9 pl-8"
                  :placeholder="
                    t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.TEMPLATE_SEARCH')
                  "
                />
              </div>
              <div
                class="max-h-44 overflow-y-auto flex flex-col divide-y divide-n-weak rounded-lg border border-n-weak"
              >
                <button
                  v-for="template in filteredTemplates"
                  :key="template.id || template.name"
                  class="flex flex-col gap-1 px-3 py-2.5 text-left hover:bg-n-alpha-2 first:rounded-t-lg last:rounded-b-lg"
                  @click="handleTemplateSelect(template)"
                >
                  <div class="flex items-center justify-between">
                    <span class="text-sm font-medium text-n-slate-12">
                      {{ template.name }}
                    </span>
                    <span class="text-xs text-n-slate-10">
                      {{ template.language }}
                    </span>
                  </div>
                  <p class="text-xs text-n-slate-11 line-clamp-2">
                    {{ getTemplateBody(template) }}
                  </p>
                </button>
                <div
                  v-if="!filteredTemplates.length"
                  class="px-3 py-4 text-sm text-n-slate-11 text-center"
                >
                  {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.TEMPLATE_EMPTY') }}
                </div>
              </div>
            </div>
          </div>

          <!-- Non-WhatsApp (API/unofficial) → free message -->
          <label v-else>
            {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.MESSAGE_LABEL') }}
            <span class="required-asterisk">*</span>
            <textarea
              v-model="customMessage"
              rows="4"
              :placeholder="
                t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.MESSAGE_PLACEHOLDER')
              "
              class="mt-1 resize-none"
            />
          </label>
        </template>

        <!-- Actions -->
        <div class="flex justify-end gap-2 pt-2">
          <Button
            faded
            slate
            :label="t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.CANCEL')"
            @click="emit('close')"
          />
          <Button
            :label="t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.CONFIRM')"
            :disabled="!canConfirm"
            @click="handleConfirm"
          />
        </div>
      </div>
    </div>
  </woot-modal>
</template>
